import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import CardHeader from '@mui/material/CardHeader';
import Card from '@mui/material/Card';
import TableContainer from '@mui/material/TableContainer';
import Pagination from '@mui/material/Pagination';
import { Grid, TableHead, Typography } from '@mui/material';
import HeaderAnalytics from './HeaderAnalytics';
import { TableNoData } from 'src/components/table';

const rowsPerPage = 4;

const renderTableCellValue = (value) => {
  const isProbablyDateString = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value);
  if (value instanceof Date || isProbablyDateString) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US');
    }
  }
  return value !== undefined ? value : '-';
};

export default function CounterWithTable({
  title,
  subheader,
  upperTableData,
  lowerTableData,
  upperTableLabels,
  lowerTableLabels,
  headerAnalyticsData,
  ...other
}) {
  const [page, setPage] = useState(1);
  const [lowerPage, setLowerPage] = useState(1);
  const now = new Date();

  const followUps = upperTableData || [];
  console.log('🚀 ~ >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>:', followUps);

  // Define the dynamic ranges based on the invoice date
  const dynamicRanges = [
    { label: '0-30 Days', min: 0, max: 30 },
    { label: '31-60 Days', min: 31, max: 60 },
    { label: '61-90 Days', min: 61, max: 90 },
    { label: '91-120 Days', min: 91, max: 120 },
  ];

  // Group invoices by their respective ranges based on invoice date
  const summaryRows = dynamicRanges.map(({ label, min, max }) => {
    console.log('check', followUps);
    const items = followUps.flatMap((item) =>
      (item.invoice_follow_ups || item.sov_follow_ups || item.follow_ups || []).map((fu) => {
        const receiptDate = new Date(fu?.date_of_receipt || fu?.invoice_date || fu?.created_at);

        return {
          amount: fu.amount_received || fu.amount_paid || 0,
          days_left: Math.floor((receiptDate - now) / (1000 * 60 * 60 * 24)),
        };
      })
    );

    console.log('check1', items);

    const filteredItems = items.filter((item) => item.days_left >= min && item.days_left <= max);
    const count = filteredItems.length;
    const total = filteredItems.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0).toFixed(2);

    return { range: label, count, total };
  });

  console.log('🚀 ~ summaryRows ~ summaryRows:', summaryRows);

  // Paginated summary rows for the first table (Summary of open invoices)
  const paginatedSummaryRows = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return summaryRows.slice(startIndex, startIndex + rowsPerPage);
  }, [page, summaryRows]);

  console.log('check2', paginatedSummaryRows);

  const paginatedLowerRows = useMemo(() => {
    if (!lowerTableData) return [];
    const startIndex = (lowerPage - 1) * rowsPerPage;
    return lowerTableData.slice(startIndex, startIndex + rowsPerPage);
  }, [lowerPage, lowerTableData]);

  // Labels for the summary rows (monthly range, count of invoices, and total amount)
  const summaryLabels = [
    { id: 'range', label: 'No of Days' },
    { id: 'count', label: 'No. of Invoices' },
    { id: 'total', label: 'Total Amount ($)' },
  ];

  const hideScroll = {
    '&::-webkit-scrollbar': { display: 'none' },
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
  };

  const fillEmptyRows = (dataLength) => {
    return Array.from({ length: rowsPerPage - dataLength }).map((_, idx) => (
      <TableRow key={`empty-${idx}`} sx={{ height: 56 }}>
        {summaryLabels.map((label) => (
          <TableCell key={label.id} />
        ))}
      </TableRow>
    ));
  };

  const noDataRow = (
    <TableRow>
      <TableCell colSpan={summaryLabels.length} align="center">
        No Data
      </TableCell>
    </TableRow>
  );

  const noDataLowerRow = (
    <TableRow>
      <TableCell colSpan={lowerTableLabels.length} align="center">
        No Data
      </TableCell>
    </TableRow>
  );

  return (
    <Card {...other} sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2, pt: 2 }}>
        <CardHeader title={title} sx={{ mb: 2, flex: '1 1 auto' }} />
      </Box>

      <Grid container justifyContent="center" alignItems="center">
        <Grid item lg={3} md={3} sm={12}>
          <HeaderAnalytics
            title={headerAnalyticsData?.name}
            totalInvoices={
              paginatedSummaryRows.length > 0
                ? paginatedSummaryRows.reduce((total, item) => total + item.count, 0)
                : headerAnalyticsData?.totalInvoices
            }
            amount={headerAnalyticsData?.amount}
            chart={{
              series: [25, 18, 12, 51, 68, 11, 39, 37, 27, 20],
              colors: headerAnalyticsData?.colors,
            }}
          />
        </Grid>

        <Grid item lg={9} md={9} sm={12}>
          <Card sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, m: 3, ml: 0 }}>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer sx={{ maxHeight: 280, overflowY: 'auto', ...hideScroll }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {summaryLabels.map((label) => (
                        <TableCell
                          key={label.id}
                          sx={{
                            minWidth: { xs: 100, sm: label.minWidth || 150 },
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'background.default',
                            zIndex: 2,
                          }}
                        >
                          {label.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSummaryRows.length > 0 ? (
                      paginatedSummaryRows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.range}</TableCell>
                          <TableCell>{row.count}</TableCell>
                          <TableCell>${row.total}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableNoData notFound="No Data" sx={{ width: '100%', textAlign: 'center' }} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Pagination
                  count={Math.ceil(summaryRows.length / rowsPerPage)}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container justifyContent="start" alignItems="start" mx={5}>
        <Typography variant="h5" color="initial">
          Paid Invoices
        </Typography>
      </Grid>

      {lowerTableData && (
        <Card sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, m: 3 }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer sx={{ maxHeight: 280, overflowY: 'auto', ...hideScroll }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {lowerTableLabels.map((label) => (
                      <TableCell
                        key={label.id}
                        sx={{
                          minWidth: { xs: 100, sm: label.minWidth || 150 },
                          position: 'sticky',
                          top: 0,
                          backgroundColor: 'background.default',
                          zIndex: 2,
                        }}
                      >
                        {label.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLowerRows.length > 0 ? (
                    paginatedLowerRows.map((row, rowIndex) => (
                      <TableRow key={row.id || rowIndex}>
                        {lowerTableLabels.map((label) => (
                          <TableCell key={label.id}>
                            {renderTableCellValue(row[label.id])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableNoData notFound="No Data" sx={{ width: '100%', textAlign: 'start' }} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                count={Math.ceil(lowerTableData.length / rowsPerPage)}
                page={lowerPage}
                onChange={(e, value) => setLowerPage(value)}
                color="primary"
              />
            </Box>
          </Box>
        </Card>
      )}
    </Card>
  );
}
